/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef } from 'react';
import { useNotification } from 'rc-notification';
import { AxiosError } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import MainContext, { ServiceOptions } from './context';
import { BasicNotification } from '../../../shared/components/Notification';
import useService from '../../../shared/hooks/useService';
import { User } from '../../../services/types/User';
import { useSessionStorageWithObject } from '../../../shared/hooks/useSessionStorage';
import { SpecialRole } from '../../../types/role';
import { ErrorPayload } from '../../../services/types/Misc';
import { getErrorMessage } from '../../../shared/misc/utils';
import mainService from '../../../services/mainService';

const MainProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [mainContextDto, mainLoading] = useService(mainService.getContext);

  // rc-notification 5.x reemplazó el singleton imperativo
  // (NotificationSystem.newInstance + ReactDOM.render, eliminado en React 19)
  // por un hook + "context holder" a renderizar en el árbol - ver Fase 4,
  // hallazgo de findDOMNode/ReactDOM.render.
  //
  // `motion` no tiene default en v5 (a diferencia de v3, que animaba
  // siempre) - sin esto ninguna notificación se anima al aparecer/
  // desaparecer. rc-motion arma las clases como `${motionName}-${estado}`
  // (confirmado leyendo getTransitionName en rc-motion/lib/util/motion.js),
  // así que 'rc-notification-fade' reusa las clases .rc-notification-fade-
  // enter/-leave ya definidas en vendor.scss (con el swap right-up/left-up
  // vía la className que notify() ya pasa por notificación) en vez de
  // duplicarlas con un nombre nuevo (Fase 4 visual-review fix).
  const [notificationApi, notificationContextHolder] = useNotification({
    closeIcon: <FontAwesomeIcon icon="times" />,
    motion: { motionName: 'rc-notification-fade' },
  });
  const noticeKeyRef = useRef(0);

  const asSuperUserModeKey = 'esg_as_super_user_mode';
  const asGuidanceServiceModeKey = 'esg_as_guidance_service_mode';

  const [asSuperUser, setAsSuperUser] = useSessionStorageWithObject(asSuperUserModeKey, false);
  const [asGuidanceService, setAsGuidanceService] = useSessionStorageWithObject(
    asGuidanceServiceModeKey,
    false
  );

  const updateAsSpecialUserMode = (mode: SpecialRole | null) => {
    setAsSuperUser(mode === SpecialRole.Superuser);
    setAsGuidanceService(mode === SpecialRole.GuidanceService);
  };

  // Antes leían sessionStorage.getItem(...) directo en vez del valor reactivo
  // que ya devuelve useSessionStorageWithObject - el `value` del contexto de
  // abajo está memoizado (useMemo, Fase 7 F7-4), así que sin asSuperUser/
  // asGuidanceService en sus deps ese memo nunca se invalidaba al cambiar de
  // modo: sessionStorage se actualizaba bien, pero ningún consumer del
  // contexto se volvía a renderizar para verlo (bug real encontrado por el
  // usuario tras F7-4 - antes del useMemo, cualquier render de MainProvider
  // reconstruía `value` entero y tapaba este problema por las dudas).
  const inSuperUserMode = () => asSuperUser;

  const inGuidanceServiceMode = () => asGuidanceService;
  useEffect(() => {
    if (
      !mainLoading &&
      mainContextDto &&
      mainContextDto.currentUser !== null &&
      !mainContextDto.currentUser.isSuperUser &&
      !mainContextDto.currentUser.isGuidanceService
    ) {
      updateAsSpecialUserMode(null);
    }
  }, [mainContextDto]);

  const notify = (
    title: string,
    message: string,
    color: string,
    icon?: IconProp,
    reportLink?: string
  ) => {
    const content = (
      <BasicNotification
        color={color}
        title={title}
        message={message}
        icon={icon || 'check-circle'}
        reportLink={reportLink}
      />
    );

    noticeKeyRef.current += 1;
    notificationApi.open({
      key: `notify-${noticeKeyRef.current}`,
      content,
      duration: 60,
      closable: true,
      placement: 'topRight',
      className: 'right-up',
    });
  };

  const notifyError = (error: AxiosError<ErrorPayload> | Error) => {
    const msg = getErrorMessage(error);
    notify('Error', msg.message, 'danger', 'exclamation-triangle', msg.reportLink);
  };

  async function service<T>(func: () => Promise<T>, options?: ServiceOptions<T>): Promise<void> {
    if (!func) {
      return;
    }
    const loader = options && options.loader ? options.loader : () => {};
    const callback = options && options.callback ? options.callback : () => {};
    const onError = options && options.onError ? options.onError : () => true;

    try {
      loader(true);
      const data = await func();
      loader(false);
      callback(data);
    } catch (error) {
      loader(false);

      if (error instanceof Error) {
        if (onError(error) !== false) {
          notifyError(error);
        }
      } else {
        // eslint-disable-next-line no-console
        console.error('Unknown error:', error);
        notifyError(new Error('Unknown error occurred'));
      }
    }
  }

  const value = useMemo(
    () => ({
      notify,
      notifyError,
      user: mainContextDto ? mainContextDto.currentUser : ({} as User),
      ezShareSiteUrl: mainContextDto ? mainContextDto.ezShareSiteUrl : '',
      countries: mainContextDto ? mainContextDto.countries : [],
      mainLoading,
      service,
      updateAsSpecialUserMode,
      inSuperUserMode,
      inGuidanceServiceMode,
    }),
    [mainContextDto, mainLoading, asSuperUser, asGuidanceService]
  );

  return (
    <MainContext.Provider value={value}>
      {notificationContextHolder}
      {children}
    </MainContext.Provider>
  );
};

export default MainProvider;
